import React, {InputHTMLAttributes, RefAttributes, useRef} from 'react'
import NumberNullable from './NumberNullable'

// unique datalist id per instance
let suggestId = 0

/**
 * A nullable number input that also offers a dropdown of suggested values (the numeric
 * analog of AutoComplete). Like AutoComplete it uses a native <datalist>, so the user can
 * still type any value freely - the suggestions are non-constraining. Suggestions are
 * shown in the order given.
 */
const NumberSuggest: React.FC<{
    value: number | null;
    change: (value: number | null) => void;
    options: number[];
    fractionDigits?: number;
    error?: boolean;
    // override how the input element is rendered (defaults to a plain input)
    children?: (attrs: InputHTMLAttributes<any> & RefAttributes<any>) => React.ReactNode;
}> = (props) => {
    const id = useRef('number-suggest-' + suggestId++)

    const format = (value: number) =>
        props.fractionDigits !== undefined ? value.toFixed(props.fractionDigits) : value.toString()

    // distinct, display-formatted suggestions
    const options: string[] = []
    props.options.forEach(o => {
        const formatted = format(o)
        if (!options.includes(formatted)) options.push(formatted)
    })

    const list = options.length ? id.current : undefined

    return <>
        <NumberNullable value={props.value}
                        change={props.change}
                        fractionDigits={props.fractionDigits}
                        error={props.error}>
            {attrs => props.children
                ? props.children({...attrs, list})
                : <input {...attrs} list={list}/>}
        </NumberNullable>
        {options.length
            ? <datalist id={id.current}>
                {options.map((o, i) => <option key={i} value={o}/>)}
            </datalist>
            : null}
    </>
}

export default NumberSuggest
